import { CardDef, DeckDef } from '../types.js';
import { getConfig } from '../config.js';
import { apiClient } from '../api-client.js';
import type { DeckWithId, GameReportRequest } from '../shared/api-types.js';
import type { AuctionMenuSummary } from '../shared/auction-types.js';

export interface PlayerData {
  collection: Record<string, number>;  // cardId → owned count
  collectionAcquiredAt: Record<string, string>;  // cardId → ISO timestamp of most recent acquisition
  decks: DeckDef[];
  activeDeckIndex: number;
  coins: number;
  gems: number;
  hasChosenStarter: boolean;
  hasReceivedStarterPack: boolean;
  gamesPlayed: number;
  wins: number;
  losses: number;
  isGuest: boolean;
  username?: string;
  // Server-side deck IDs, parallel to decks[]
  deckIds: string[];
  // Stage IDs the player has won at least once. Drives the campaign UI's
  // locked / available / completed treatment.
  beatenStageIds: string[];
  // Generic single-use redemption credits keyed by ticket id slug.
  // Mirrors the server's `user_tickets` table. `lastUsed` is the ISO
  // timestamp of the most recent claim (or null) — drives availability
  // of time-gated tickets like the daily-gems subscription. See
  // `src/shared/tickets.ts` for display names.
  tickets: Record<string, { count: number; lastUsed: string | null }>;
  // Card id the player picked to represent their account. Set during
  // the starter flow to the legendary that came with the chosen deck;
  // editable on the settings panel later. Null for brand-new guests
  // who haven't picked a starter yet.
  profileCardId: string | null;
  // Account-created timestamp (ISO-Z). Surfaced on the settings panel
  // so the player can see how long they've been around.
  createdAt: string;
  // When the player can next change their username, ISO-Z. Null when
  // no active cooldown (never renamed, or cooldown has elapsed).
  // Drives the settings rename form's disabled state.
  usernameChangeAvailableAt: string | null;
  // Number of deck slots the player has purchased beyond config.maxDecks.
  // Effective cap on deck-list size = maxDecks + extraDeckSlots.
  extraDeckSlots: number;
  // Auction house state summary, or null while it hasn't loaded yet /
  // the player is a guest / the feature is locked. Drives the menu
  // tile's pip and secondary line. Fetched on load AND refreshed
  // whenever we return to the main menu.
  auctionSummary: AuctionMenuSummary | null;
}

// ── Load from server ────────────────────────────────────────────

export async function loadPlayerData(): Promise<PlayerData> {
  // Get or create session
  let profile = await apiClient.getSession();
  if (!profile) {
    profile = await apiClient.createGuest();
  }

  const data: PlayerData = {
    collection: {},
    collectionAcquiredAt: {},
    decks: [],
    activeDeckIndex: 0,
    coins: profile.coins,
    gems: profile.gems,
    hasChosenStarter: profile.hasChosenStarter,
    hasReceivedStarterPack: profile.hasReceivedStarterPack,
    gamesPlayed: profile.gamesPlayed,
    wins: profile.wins,
    losses: profile.losses,
    isGuest: profile.isGuest,
    username: profile.username,
    deckIds: [],
    beatenStageIds: profile.beatenStageIds ?? [],
    tickets: profile.tickets ?? {},
    profileCardId: profile.profileCardId ?? null,
    createdAt: profile.createdAt,
    usernameChangeAvailableAt: profile.usernameChangeAvailableAt ?? null,
    extraDeckSlots: profile.extraDeckSlots ?? 0,
    auctionSummary: null,
  };

  if (profile.hasChosenStarter) {
    const [collResp, decksResp] = await Promise.all([
      apiClient.getCollection(),
      apiClient.getDecks(),
    ]);
    data.collection = collResp.collection;
    data.collectionAcquiredAt = collResp.acquiredAt ?? {};
    applyDecks(data, decksResp.decks, decksResp.activeDeckId);
  }

  return data;
}

/** Fetch the caller's custom cards and add them to an existing catalog Map. */
export async function mergeCustomCardsIntoCatalog(catalog: Map<string, CardDef>): Promise<void> {
  try {
    const resp = await apiClient.getMyCustomCards();
    for (const card of resp.cards) {
      catalog.set(card.id, card);
    }
  } catch (e) {
    console.warn('Failed to load custom cards:', e);
  }
}

/** Fetch CardDefs for any IDs in the input that are NOT already in the
 *  local catalog, and merge them in. Used by the tournament ladder /
 *  detail views to resolve OTHER players' profile-card avatars: the
 *  caller's catalog only carries premade cards plus their own customs,
 *  so a profile card created by a different player is unknown locally.
 *  Premade-shaped (slug) IDs are skipped client-side — they should
 *  already be in the catalog, and the server filters them out anyway. */
export async function mergeMissingCardsByIds(
  catalog: Map<string, CardDef>,
  ids: Array<string | null | undefined>,
): Promise<void> {
  const missing: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (catalog.has(id)) continue;
    // Only GUID-shaped (custom) IDs are worth a server round-trip; premade
    // slugs in the local catalog mean it's just absent, which is fine.
    if (!/^[0-9a-f]{32}$/.test(id)) continue;
    missing.push(id);
  }
  if (missing.length === 0) return;
  try {
    const resp = await apiClient.getCustomCardsByIds(missing);
    for (const card of resp.cards) {
      catalog.set(card.id, card);
    }
  } catch (e) {
    console.warn('Failed to load referenced custom cards:', e);
  }
}

/** Refresh the auction-house summary on the passed PlayerData. Silent
 *  on failure — the auction house is a secondary surface and a menu
 *  render shouldn't fail just because /api/auctions/summary is
 *  briefly unavailable. Setting `auctionSummary = null` on failure so
 *  the tile falls back to the "no state" render. */
export async function refreshAuctionSummary(data: PlayerData): Promise<void> {
  if (data.isGuest) {
    // Signed-in only. Don't hit the endpoint (which would 403).
    data.auctionSummary = null;
    return;
  }
  try {
    data.auctionSummary = await apiClient.getAuctionSummary();
  } catch {
    data.auctionSummary = null;
  }
}

export async function refreshPlayerProfile(data: PlayerData): Promise<void> {
  const profile = await apiClient.getSession();
  if (profile) {
    data.coins = profile.coins;
    data.gems = profile.gems;
    data.gamesPlayed = profile.gamesPlayed;
    data.wins = profile.wins;
    data.losses = profile.losses;
    data.hasChosenStarter = profile.hasChosenStarter;
    data.hasReceivedStarterPack = profile.hasReceivedStarterPack;
    data.isGuest = profile.isGuest;
    data.username = profile.username;
    data.beatenStageIds = profile.beatenStageIds ?? [];
    data.tickets = profile.tickets ?? {};
  }
}

export async function registerAccount(data: PlayerData, username: string, password: string): Promise<void> {
  const profile = await apiClient.register(username, password);
  data.isGuest = profile.isGuest;
  data.username = profile.username;
}

export async function signIn(username: string, password: string): Promise<PlayerData> {
  await apiClient.login(username, password);
  return loadPlayerData();
}

/** Clear the current session and return a fresh guest PlayerData. The
 *  original account's server-side data is untouched — `signIn(username, pw)`
 *  restores it. Call sites should also reload the card catalog so the
 *  signed-out user's custom cards aren't carried over. Also wipes the local
 *  tutorial-seen markers so the new player gets the onboarding banners
 *  again. */
export async function signOut(): Promise<PlayerData> {
  await apiClient.logout();
  resetPlayerData();
  return loadPlayerData();
}

function applyDecks(data: PlayerData, serverDecks: DeckWithId[], activeDeckId: string | null): void {
  data.decks = serverDecks.map(d => ({
    name: d.name,
    cards: d.cards.map(c => ({ id: c.id, count: c.count })),
  }));
  data.deckIds = serverDecks.map(d => d.id);
  const activeIdx = activeDeckId ? serverDecks.findIndex(d => d.id === activeDeckId) : 0;
  data.activeDeckIndex = activeIdx >= 0 ? activeIdx : 0;
}

// ── Server-backed mutations ─────────────────────────────────────

export async function initFromStarterDeck(data: PlayerData, deck: DeckDef): Promise<void> {
  const resp = await apiClient.chooseStarter(deck.name, deck.cards);
  data.collection = resp.collection;
  data.collectionAcquiredAt = resp.acquiredAt ?? {};
  data.coins = resp.coins;
  data.hasChosenStarter = true;
  applyDecks(data, resp.decks, resp.activeDeckId);
}

/** Buy a store offer. Handles both payment modes:
 *  - USD-paid: server credits the grants with no real billing
 *    (alpha mode); the client confirm dialog is the only payment
 *    surface.
 *  - Gem-paid: server verifies and decrements gems atomically before
 *    crediting the grants (typically coins).
 *  Syncs all three balance dimensions (gems / coins / tickets) from
 *  the response so a single offer that grants two of them lands
 *  consistently on the local PlayerData. */
export async function buyStoreOffer(data: PlayerData, offerId: string): Promise<void> {
  const resp = await apiClient.buyStoreOffer(offerId);
  data.gems = resp.newGemsBalance;
  data.coins = resp.newCoinsBalance;
  data.tickets = resp.newTickets ?? {};
}

/** Claim today's daily gems if the subscription is active and unclaimed.
 *  Returns the gems granted (and updated balance) on success; throws if
 *  no claim was available (server returns 400). The caller is expected
 *  to gate the call on `canClaimDailyGems` first to keep the UI honest,
 *  but the server is authoritative — a stale UI that calls in anyway
 *  will get the 400 and report it. */
export async function claimDailyGems(data: PlayerData): Promise<{ gemsGranted: number; newGems: number }> {
  const resp = await apiClient.claimDailyGems();
  data.gems = resp.newGemsBalance;
  data.tickets = resp.newTickets ?? {};
  return { gemsGranted: resp.gemsGranted, newGems: data.gems };
}

/** List one of the player's owned custom cards for auction. Mirrors
 *  the server-side effects locally so the deck editor / picker /
 *  ticket counts stay in sync without a full refresh:
 *   - Decrement the collection count for the card (delete if zero).
 *   - Spend one `auction-listing` ticket.
 *   - Update the local auctionSummary's activeSales counter so the
 *     menu widget picks up the new listing.
 *  The server also invalidates any of the player's decks that
 *  reference the card, but the client doesn't currently track
 *  is_valid — the collection-count check at deck save is the
 *  enforcement point, so the invalid deck will fail to save until
 *  the player edits it. */
export async function listCardForAuction(data: PlayerData, cardId: string): Promise<{ reservePrice: number; endsAt: string }> {
  const resp = await apiClient.listAuctionCard({ cardId });
  const owned = (data.collection[cardId] ?? 0) - 1;
  if (owned <= 0) {
    delete data.collection[cardId];
    delete data.collectionAcquiredAt[cardId];
  } else {
    data.collection[cardId] = owned;
  }
  const ticket = data.tickets['auction-listing'];
  if (ticket) {
    const remaining = ticket.count - 1;
    if (remaining <= 0) delete data.tickets['auction-listing'];
    else data.tickets['auction-listing'] = { ...ticket, count: remaining };
  }
  if (data.auctionSummary) {
    data.auctionSummary = { ...data.auctionSummary, activeSales: data.auctionSummary.activeSales + 1 };
  }
  // Clamp local decks so the menu tile count and the deck editor's
  // "N/50" both reflect ownership immediately. The server side does
  // the same clamp on /api/collection reads, but that only helps on
  // next page load; without this, the current session's playerData
  // still carries the stale entry and the deck reads as 50/50 with a
  // phantom card. Reduce the entry to the new owned count; drop it
  // entirely if the player no longer owns any copies.
  const newOwned = data.collection[cardId] ?? 0;
  for (const deck of data.decks) {
    for (let i = deck.cards.length - 1; i >= 0; i--) {
      const e = deck.cards[i];
      if (e.id !== cardId) continue;
      if (newOwned <= 0) deck.cards.splice(i, 1);
      else e.count = Math.min(e.count, newOwned);
    }
  }
  return { reservePrice: resp.reservePrice, endsAt: resp.endsAt };
}

export async function buyPack(data: PlayerData, packId: string, useTicket?: string): Promise<string[]> {
  const resp = await apiClient.buyPack(packId, useTicket);
  data.coins = resp.newBalance;
  data.tickets = resp.newTickets ?? {};
  const now = new Date().toISOString();
  for (const cardId of resp.cards) {
    data.collection[cardId] = (data.collection[cardId] ?? 0) + 1;
    data.collectionAcquiredAt[cardId] = now;
  }
  return resp.cards;
}

export type GameReportMode =
  | { type: 'skirmish' }
  | { type: 'campaign'; campaignId: string; stageId: string }
  // gameRuleId / deckRuleId are captured at click time from the
  // tournament summary and travel with the mode through the whole
  // match, so any cache churn between click and match-start can't
  // silently downgrade the rules to skirmish defaults.
  | { type: 'tournament'; tournamentId: string; matchId: string; gameRuleId: string; deckRuleId: string }
  // Practice match — player's active deck vs a chosen deck for
  // testing. No reward, no game_results row, no affinity bump. Carries
  // the opponent deck inline so the engine can build initial state
  // without a server-side opponent pick. The active deck is always
  // resolved client-side at match start.
  | { type: 'practice'; opponentDeck: DeckDef };

export async function reportGame(
  data: PlayerData,
  won: boolean,
  mode: GameReportMode,
  actionLog?: import('../types.js').GameAction[],
  cardsPlayed?: string[],
): Promise<{ coins: number; gems: number; cardsAwarded: CardDef[]; ticketsAwarded: { id: string; count: number }[] }> {
  if (mode.type === 'tournament') {
    throw new Error('reportGame is not for tournament mode — call apiClient.reportTournamentMatch directly');
  }
  if (mode.type === 'practice') {
    throw new Error('reportGame is not for practice mode — practice matches are not reported');
  }
  const req: GameReportRequest = mode.type === 'skirmish'
    ? { won, mode: 'skirmish', actionLog, cardsPlayed }
    : { won, mode: 'campaign', campaignId: mode.campaignId, stageId: mode.stageId, actionLog, cardsPlayed };
  const resp = await apiClient.reportGame(req);
  data.coins = resp.newBalance;
  data.gems = resp.newGemsBalance;
  data.gamesPlayed = resp.gamesPlayed;
  data.wins = resp.wins;
  data.losses = resp.losses;
  data.hasReceivedStarterPack = resp.hasReceivedStarterPack;
  data.beatenStageIds = resp.beatenStageIds ?? [];
  data.tickets = resp.newTickets ?? {};
  const now = new Date().toISOString();
  for (const card of resp.cardsAwarded) {
    data.collection[card.id] = (data.collection[card.id] ?? 0) + 1;
    data.collectionAcquiredAt[card.id] = now;
  }
  return {
    coins: resp.coinsAwarded,
    gems: resp.gemsAwarded,
    cardsAwarded: resp.cardsAwarded,
    ticketsAwarded: resp.ticketsAwarded ?? [],
  };
}

export async function saveDeck(data: PlayerData, deckIndex: number, deck: DeckDef): Promise<void> {
  const deckId = data.deckIds[deckIndex];
  const resp = await apiClient.saveDeck(deckId, deck.name, deck.cards);
  data.decks[deckIndex] = { name: resp.deck.name, cards: resp.deck.cards.map(c => ({ id: c.id, count: c.count })) };
  data.deckIds[deckIndex] = resp.deck.id;
}

export async function createDeck(data: PlayerData, name: string, cards: { id: string; count: number }[]): Promise<void> {
  const resp = await apiClient.saveDeck(undefined, name, cards);
  data.decks.push({ name: resp.deck.name, cards: resp.deck.cards.map(c => ({ id: c.id, count: c.count })) });
  data.deckIds.push(resp.deck.id);
}

export async function deleteDeck(data: PlayerData, deckIndex: number): Promise<void> {
  const deckId = data.deckIds[deckIndex];
  // Unsaved-deck case: `new-deck` pushes a placeholder entry into
  // `decks` + `deckIds` (empty string id) so the player can immediately
  // navigate into the builder. Until they hit Save, the server doesn't
  // know about the row, so calling apiClient.deleteDeck('') would 404
  // OR (worse) trip the server's "only deck" guard since the
  // never-saved row doesn't count server-side. Just splice locally.
  if (deckId !== '') {
    await apiClient.deleteDeck(deckId);
  }
  data.decks.splice(deckIndex, 1);
  data.deckIds.splice(deckIndex, 1);

  if (data.activeDeckIndex >= data.decks.length) {
    data.activeDeckIndex = data.decks.length - 1;
  }
  if (data.activeDeckIndex === deckIndex) {
    data.activeDeckIndex = 0;
  } else if (data.activeDeckIndex > deckIndex) {
    data.activeDeckIndex--;
  }
}

export async function setActiveDeck(data: PlayerData, deckIndex: number): Promise<void> {
  const deckId = data.deckIds[deckIndex];
  await apiClient.setActiveDeck(deckId);
  data.activeDeckIndex = deckIndex;
}

export function resetPlayerData(): void {
  // Clear tutorials from localStorage (tutorials stay client-side)
  localStorage.removeItem('cardverse-tutorials');
}

// ── Pure helpers (unchanged) ────────────────────────────────────

export function getActiveDeck(data: PlayerData): DeckDef | null {
  return data.decks[data.activeDeckIndex] ?? null;
}

export function getDeckCardCount(deck: DeckDef): number {
  return deck.cards.reduce((sum, c) => sum + c.count, 0);
}

/** Check that every card in the deck is still owned by the player.
 *  Returns `{ ok: true }` when the deck is playable; otherwise the
 *  list of card IDs the player no longer owns enough copies of.
 *
 *  Guards the play-time path against decks that reference cards the
 *  player has since parted with (auctioned, admin-removed, etc.).
 *  Ownership-based check rather than any server flag so it catches
 *  every path that pulls a card out of the collection. */
export function checkDeckPlayable(deck: DeckDef, collection: Record<string, number>): { ok: true } | { ok: false; missing: string[] } {
  const missing: string[] = [];
  for (const entry of deck.cards) {
    const owned = collection[entry.id] ?? 0;
    if (entry.count > owned) missing.push(entry.id);
  }
  return missing.length === 0 ? { ok: true } : { ok: false, missing };
}

export function canAddCardToDeck(data: PlayerData, deckIndex: number, cardId: string): boolean {
  const deck = data.decks[deckIndex];
  if (!deck) return false;

  const config = getConfig();
  const totalCards = getDeckCardCount(deck);
  if (totalCards >= config.minDeckSize) return false;

  const existing = deck.cards.find(c => c.id === cardId);
  const currentCount = existing?.count ?? 0;
  if (currentCount >= config.maxCopiesPerCard) return false;

  const owned = data.collection[cardId] ?? 0;
  if (owned <= 0) return false;

  return true;
}

export function addCardToDeck(deck: DeckDef, cardId: string): void {
  const existing = deck.cards.find(c => c.id === cardId);
  if (existing) {
    existing.count++;
  } else {
    deck.cards.push({ id: cardId, count: 1 });
  }
}

export function removeCardFromDeck(deck: DeckDef, cardId: string): void {
  const existing = deck.cards.find(c => c.id === cardId);
  if (!existing) return;
  existing.count--;
  if (existing.count <= 0) {
    deck.cards = deck.cards.filter(c => c.id !== cardId);
  }
}
